import {produce} from 'immer';
import type {FetchResult} from '@apollo/client';

import type {
    CreateSessionPackageMutationResult,
    CreateSessionPackageMutationVariables,
} from 'web-app/api/clients/rest-types/create-session-package/create-session-package';
import {useRestMutation} from 'web-app/react/hooks/use-rest-mutation';
import type {SessionsResponse} from 'web-app/api/clients/rest-types/sessions/sessions-query-data';
import {updateApolloCache} from 'web-app/util/graphql';
import type {SessionsVariables} from 'web-app/api/clients/rest-types/sessions/sessions';

import {CreateSessionPackage} from '../queries/create-session-package.rest-mutation';
import {SessionsQuery} from '../queries/sessions.rest-query';
import type {ApiSessionPackageCreation} from '../api-types';
import type {AfterNewTuitionSubmission} from '../types';

type Submit = (
    session: ApiSessionPackageCreation,
    afterSubmission?: AfterNewTuitionSubmission,
    shouldUpdateCache?: boolean,
) => Promise<FetchResult<CreateSessionPackageMutationResult>>;

type UseSubmitTuition = () => {
    submit: Submit;
    isSessionPackageCreationLoading: boolean;
};

export const useSubmitTuition: UseSubmitTuition = () => {
    const [createSessionPackage, {loading: isSessionPackageCreationLoading}] = useRestMutation<
        CreateSessionPackageMutationResult,
        CreateSessionPackageMutationVariables
    >(CreateSessionPackage);

    const submit: Submit = (session, afterSubmission, shouldUpdateCache = false) => {
        return createSessionPackage({
            variables: {
                input: {
                    session,
                },
            },
            update: shouldUpdateCache
                ? updateApolloCache<SessionsResponse, SessionsVariables>()({
                      type: 'query',
                      node: SessionsQuery,
                      cacheVariables: {
                          institutionId: session.institutionId,
                          validOn: '',
                          includeDiscontinued: false,
                          behaviors: 0,
                          includeHiddenByUser: true,
                      },
                      updater(cache, result) {
                          return produce(cache, cacheDraft => {
                              const updatedSessions = [
                                  ...cache.result.sessions,
                                  {...result.createSessionPackage.session, hiddenByUser: false},
                              ];
                              cacheDraft.result.sessions = updatedSessions;
                          });
                      },
                  })
                : undefined,
            onCompleted: result => {
                afterSubmission?.(result);
            },
        });
    };

    return {submit, isSessionPackageCreationLoading};
};
